import { zodResolver } from '@hookform/resolvers/zod';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React from 'react';
import { useForm } from 'react-hook-form';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';
import { z } from 'zod';

import { useLoginCustomerConfirmPassword } from '@/api/customers/use-login-customer-confirm-password';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

const confirmNewPasswordFormSchema = z
  .object({
    newPassword: z.string().min(8, 'Minimum 8 characters'),
    confirmNewPassword: z.string().min(8, 'Minimum 8 characters'),
    token: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords must match.',
    path: ['confirmNewPassword'],
  });

export type ConfirmNewPasswordFormType = z.infer<
  typeof confirmNewPasswordFormSchema
>;

export type ConfirmNewPasswordFormProps = {
  token: string;
  newPassword: string;
};

export default function ConfirmNewPassword() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const local = useLocalSearchParams();

  const { mutate: loginCustomerConfirmPass, isPending: isConfirmingPass } =
    useLoginCustomerConfirmPassword();

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    getValues,
    setError,
    reset,
    formState: { isValid },
  } = useForm<ConfirmNewPasswordFormType>({
    resolver: zodResolver(confirmNewPasswordFormSchema),
    defaultValues: {
      token: local.token as string,
    },
  });

  const handleContinue = (data: ConfirmNewPasswordFormProps) => {
    loginCustomerConfirmPass(
      { ...data, token: local.token as string },
      {
        onSuccess: () => {
          Toast.show({
            text1: 'Password updated successfully',
            type: 'success',
          });

          reset();
          router.replace('/login');
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/login');
    }
  };

  const handleBlur = (fieldName: string) => {
    trigger(fieldName);

    const values = getValues();

    try {
      confirmNewPasswordFormSchema.parse(values);
    } catch (error) {
      if (error instanceof z.ZodError) {
        console.log('error', error);
        setError(fieldName, { type: 'manual', message: error.message });
      }
    }
  };

  if (isConfirmingPass) {
    return <Loader />;
  }

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.background_1 }}>
      <View style={styles.header}>
        <NavigationButton
          type={NavigationButtonTypes.back_with_text}
          onPress={handleBack}
        />
      </View>

      <KeyboardAwareScrollView
        style={{
          backgroundColor: theme.colors.background_1,
        }}
        contentContainerStyle={styles.contentContainer}
      >
        <View style={styles.container}>
          <View style={styles.formContainer}>
            <Text tx="confirmNewPassword.subtitle" type="title1" />

            <View style={styles.inputsContainer}>
              <ControlledInput
                control={control}
                placeholder="Enter Password"
                name="newPassword"
                label="New password"
                secureTextEntry
                onBlur={() => {
                  handleBlur('newPassword');
                }}
                onChangeText={(text) => {
                  trigger('newPassword');
                  setValue('newPassword', text);
                }}
              />

              <ControlledInput
                control={control}
                placeholder="Enter Password"
                name="confirmNewPassword"
                label="Confirm password"
                secureTextEntry
                onBlur={() => {
                  handleBlur('confirmNewPassword');
                }}
                onChangeText={(text) => {
                  trigger('confirmNewPassword');
                  setValue('confirmNewPassword', text);
                }}
              />
            </View>

            <View style={styles.buttonContainer}>
              <Button
                tx="confirmNewPassword.button"
                type="primary"
                onPress={handleSubmit(handleContinue)}
                disabled={isValid === false}
              />
            </View>
          </View>
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
}

const stylesheet = createStyleSheet(() => ({
  header: {
    width: { xs: '100%', md: 600 },
    alignSelf: 'center',
    paddingTop: UnistylesRuntime.insets.top + 8,
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  contentContainer: {
    alignItems: {
      md: 'center',
    },
  },
  container: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingBottom: 24,
    gap: 16,
    width: {
      md: 600,
    },
  },
  formContainer: {
    marginTop: 40,
    gap: 16,
  },
  inputsContainer: {
    gap: 16,
  },
  buttonContainer: {
    gap: 16,
    alignItems: 'center',
  },
}));
